// Helper to safely render customer name
function renderCustomerName(customer) {
  // Debug: log what is being rendered
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.log('Render customer:', customer);
  }
  if (!customer) return '-';
  if (typeof customer === 'string') return customer;
  if (typeof customer === 'object' && customer !== null) {
    if (Array.isArray(customer)) return '-';
    if (typeof customer.name === 'string') return customer.name;
    // Defensive: stringify any object
    return '-';
  }
  if (typeof customer === 'number') return String(customer);
  // Fallback: always return a string
  return String(customer);
}
import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, TextField, MenuItem, FormControl, InputLabel, Select, Pagination, Chip, Box, Tooltip, IconButton
} from '@mui/material';
import { StickyNote2 as NotesIcon, CreditCard as CardIcon, Payments as CashIcon } from '@mui/icons-material';
import InvoiceDialog from './InvoiceDialog';
import { fetchCurrencySetting } from '../utils/fetchCurrencySetting';

const sortOptions = [
  { value: 'date-desc', label: 'Date (Newest)' },
  { value: 'date-asc', label: 'Date (Oldest)' },
  { value: 'total-desc', label: 'Total (High-Low)' },
  { value: 'total-asc', label: 'Total (Low-High)' },
];

const Transactions = ({ token }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date-desc');
  const [selected, setSelected] = useState(null);
  const [selectedCustomerPending, setSelectedCustomerPending] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [currency, setCurrency] = useState('USD');

  // Fetch currency setting
  useEffect(() => {
    fetchCurrencySetting().then(setCurrency);
  }, []);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return amount.toLocaleString(undefined, { style: 'currency', currency });
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/sales', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setTransactions(Array.isArray(data) ? data : []);
      } catch {
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchTransactions();
  }, [token]);

  const filtered = transactions
    .filter(sale => {
      let customerName = '';
      if (typeof sale.customer === 'string') customerName = sale.customer;
      else if (sale.customer && typeof sale.customer === 'object' && !Array.isArray(sale.customer)) customerName = sale.customer.name || '';
      return (
        customerName.toLowerCase().includes(search.toLowerCase()) ||
        sale.user?.username?.toLowerCase().includes(search.toLowerCase()) ||
        sale.products.some(p => p.product?.name?.toLowerCase().includes(search.toLowerCase()))
      );
    })
    .sort((a, b) => {
      if (sort === 'date-desc') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sort === 'date-asc') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sort === 'total-desc') return b.total - a.total;
      if (sort === 'total-asc') return a.total - b.total;
      return 0;
    });

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const pagedTransactions = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 if search, sort, or pageSize changes
  useEffect(() => { setPage(1); }, [search, sort, pageSize]);

  // Fetch customer pending info when a sale is selected
  useEffect(() => {
    if (!selected || !selected.customer || !token) {
      setSelectedCustomerPending(null);
      return;
    }

    const customerId = typeof selected.customer === 'object' ? selected.customer._id : selected.customer;
    if (!customerId) {
      setSelectedCustomerPending(null);
      return;
    }

    fetch(`/api/customers/${customerId}/pending`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setSelectedCustomerPending({
          total: data.totalPending || 0,
          count: data.count || 0
        });
      })
      .catch(() => {
        setSelectedCustomerPending(null);
      });
  }, [selected, token]);

  return (
    <Paper sx={{ p: 4, mt: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>Transactions</Typography>
        <TextField
          size="small"
          placeholder="Search customer, cashier, or product..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="page-size-label">Show</InputLabel>
          <Select
            labelId="page-size-label"
            value={pageSize}
            label="Show"
            onChange={e => setPageSize(Number(e.target.value))}
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          select
          label="Sort"
          value={sort}
          onChange={e => setSort(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {sortOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
      </Stack>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Cashier</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11}>Loading...</TableCell></TableRow>
            ) : pagedTransactions.length === 0 ? (
              <TableRow><TableCell colSpan={11}>No transactions found.</TableCell></TableRow>
            ) : (
              pagedTransactions.map((sale) => {
                // Check if transaction has discounts (either global discount or item-level discounts)
                const hasGlobalDiscount = sale.discount > 0;
                const hasItemDiscounts = sale.products.some(item => {
                  const originalPrice = item.product?.price;
                  return originalPrice && item.price < originalPrice;
                });
                const hasAnyDiscount = hasGlobalDiscount || hasItemDiscounts;

                return (
                <TableRow
                  key={sale._id}
                  hover
                  sx={{ 
                    cursor: 'pointer', 
                    backgroundColor: sale.paymentStatus === 'Pending' ? '#fffbe6' : hasAnyDiscount ? '#f0f9ff' : undefined,
                    borderLeft: hasAnyDiscount ? '4px solid #0ea5e9' : undefined
                  }}
                  onClick={() => setSelected(sale)}
                >
                  <TableCell>{new Date(sale.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    {renderCustomerName(sale.customer)}
                  </TableCell>
                  <TableCell>{sale.user?.username || '-'}</TableCell>
                  <TableCell>
                    <Box>
                      {sale.products.map((item, idx) => {
                        const originalPrice = item.product?.price;
                        const costPrice = item.product?.costPrice || 0;
                        const isDiscounted = originalPrice && item.price < originalPrice;
                        const isBelowCost = costPrice > 0 && item.price < costPrice;
                        const discountPercent = isDiscounted 
                          ? Math.round(((originalPrice - item.price) / originalPrice) * 100)
                          : 0;
                        const lossAmount = isBelowCost ? (costPrice - item.price) : 0;

                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              mb: idx < sale.products.length - 1 ? 0.5 : 0,
                              py: 0.5,
                              px: (isDiscounted || isBelowCost) ? 1 : 0,
                              bgcolor: isBelowCost ? '#fee2e2' : isDiscounted ? '#dcfce7' : 'transparent',
                              borderRadius: 1,
                              border: isBelowCost ? '1px solid #fca5a5' : isDiscounted ? '1px solid #86efac' : 'none'
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {item.product?.name || '-'} x {item.quantity} @ {formatCurrency(item.price)}
                            </Typography>
                            {isBelowCost && (
                              <Chip 
                                label={`LOSS ${formatCurrency(lossAmount)}`}
                                size="small"
                                sx={{ 
                                  bgcolor: '#dc2626',
                                  color: 'white',
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                            {isDiscounted && !isBelowCost && (
                              <Chip 
                                label={`-${discountPercent}%`}
                                size="small"
                                color="success"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                            {isBelowCost && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through',
                                  color: 'text.secondary',
                                  fontSize: '0.7rem'
                                }}
                              >
                                Cost: {formatCurrency(costPrice)}
                              </Typography>
                            )}
                            {isDiscounted && !isBelowCost && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through',
                                  color: 'text.secondary',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {formatCurrency(originalPrice)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                      {hasGlobalDiscount && (
                        <Chip 
                          label={`Global Discount: ${formatCurrency(sale.discount)}`}
                          size="small"
                          color="warning"
                          sx={{ 
                            mt: 0.5,
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>{formatCurrency(sale.total)}</Typography>
                      {hasAnyDiscount && (
                        <Chip 
                          label="DISCOUNTED"
                          size="small"
                          sx={{ 
                            bgcolor: '#0ea5e9',
                            color: 'white',
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{sale.discount !== undefined ? sale.discount.toFixed(2) : '-'}</TableCell>
                  <TableCell>
                    {sale.paymentStatus === 'Pending' ? '-' : (sale.paidAmount !== undefined ? sale.paidAmount.toFixed(2) : '-')}
                  </TableCell>
                  <TableCell>
                    {sale.paymentStatus === 'Pending' ? '-' : (sale.change !== undefined ? sale.change.toFixed(2) : '-')}
                  </TableCell>
                  {/* Status indicator */}
                  <TableCell>
                    {sale.paymentStatus === 'Pending' ? (
                      <Chip
                        label="Unpaid"
                        size="small"
                        sx={{
                          bgcolor: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Chip
                          label="Paid"
                          size="small"
                          sx={{
                            bgcolor: '#d1fae5',
                            color: '#065f46',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                        <Tooltip title={sale.paymentMethod === 'card' ? 'Card Payment' : 'Cash Payment'} arrow>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            {sale.paymentMethod === 'card' ? (
                              <CardIcon sx={{ fontSize: '1.1rem', color: '#3b82f6' }} />
                            ) : (
                              <CashIcon sx={{ fontSize: '1.1rem', color: '#10b981' }} />
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  {/* Notes */}
                  <TableCell>
                    {sale.notes ? (
                      <Tooltip title={sale.notes} arrow placement="left">
                        <IconButton size="small" color="info">
                          <NotesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  {/* Last Updated - Show for unpaid bills */}
                  <TableCell>
                    {sale.paymentStatus === 'Pending' && sale.updatedAt && sale.updatedAt !== sale.createdAt ? (
                      <span style={{ fontSize: '0.875rem', color: '#666' }}>
                        {new Date(sale.updatedAt).toLocaleString()}
                      </span>
                    ) : '-'}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="center" alignItems="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </Stack>
      <InvoiceDialog 
        open={!!selected} 
        onClose={() => setSelected(null)} 
        sale={selected} 
        customerPending={selectedCustomerPending}
      />
    </Paper>
  );
};

export default Transactions;
